/**
 * GRAPHICS TUNING — the one place for the look's tweakable numbers, plus the
 * player-facing quality preset (Settings → Graphics quality).
 *
 * `GFX` is a plain mutable object: the lil-gui debug panel (`gfxPanel.ts`, F7)
 * edits it live and then calls the active renderer's `applyGfxConfig()`. Panel
 * edits can be saved to localStorage (`neonpurge.gfxTune`) so a tuned value
 * survives reloads on the tuner's machine; "Reset" in the panel drops them.
 * The DEFAULTS below are what every player gets — change them here to ship a
 * new tune.
 *
 * Nothing here runs per frame. Reads are plain property loads.
 */

export interface GfxTune {
  /** Renderer tone-mapping exposure (ACES). */
  exposure: number;
  /** Bloom on neon maps (UnrealBloomPass strength / radius / threshold). */
  bloomStrengthNeon: number;
  bloomRadiusNeon: number;
  bloomThresholdNeon: number;
  /** Bloom on day maps — restrained so a bright scene never whites out. */
  bloomStrengthDay: number;
  bloomRadiusDay: number;
  bloomThresholdDay: number;
  /** Fog thickness multiplier. The fog is LINEAR (a FogExp2 swap is a
   *  shader-program cache-key change → full-scene recompile, see
   *  renderer.ts setUnderwater), so "density" scales near/far: 2 = fog starts
   *  and ends at half the distance, 0.5 = twice as far. */
  fogDensity: number;
  /** Screen-space AO (N8AO, High preset only). */
  aoIntensity: number;
  /** World-space AO radius in metres. Keep small: the viewmodel shares the
   *  depth buffer, and a large radius haloes the gun against close walls. */
  aoRadius: number;
  aoDistanceFalloff: number;
  /** Scene environment (image-based light) strength. */
  envIntensity: number;
  /** Use the photographed HDRI sky for day-map lighting/reflections (High). */
  hdri: boolean;
  /** Shadow acne / peter-panning guards. */
  shadowBias: number;
  shadowNormalBias: number;
}

export const GFX_DEFAULTS: Readonly<GfxTune> = Object.freeze({
  exposure: 1.04,
  bloomStrengthNeon: 0.55,
  bloomRadiusNeon: 0.7,
  bloomThresholdNeon: 0.55,
  bloomStrengthDay: 0.22,
  bloomRadiusDay: 0.5,
  bloomThresholdDay: 0.9,
  fogDensity: 1,
  aoIntensity: 2.2,
  aoRadius: 1.6,
  aoDistanceFalloff: 1.0,
  envIntensity: 0.55,
  hdri: true,
  shadowBias: -0.0004,
  shadowNormalBias: 0.02,
});

const TUNE_KEY = "neonpurge.gfxTune";

function loadTune(): GfxTune {
  const t: GfxTune = { ...GFX_DEFAULTS };
  try {
    const raw = localStorage.getItem(TUNE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<GfxTune>;
      for (const k of Object.keys(t) as (keyof GfxTune)[]) {
        if (typeof saved[k] === typeof t[k]) (t as unknown as Record<string, unknown>)[k] = saved[k];
      }
    }
  } catch { /* private mode / bad JSON → defaults */ }
  return t;
}

/** Live tuning values. Mutate, then call `applyGfxConfig()` on the renderer. */
export const GFX: GfxTune = loadTune();

export function saveGfxTune(): void {
  try { localStorage.setItem(TUNE_KEY, JSON.stringify(GFX)); } catch { /* private mode */ }
}
export function resetGfxTune(): void {
  try { localStorage.removeItem(TUNE_KEY); } catch { /* private mode */ }
  Object.assign(GFX, GFX_DEFAULTS);
}

// ---------------------------------------------------------------------------
// QUALITY PRESET
//
// "auto" is the historical behaviour: hardware probe at boot + the adaptive
// cost ladder. The explicit presets set the CEILING; the adaptive ladder still
// runs underneath every preset (it only ever lowers cost when frames are
// actually late), because a stutter-free frame rate is the higher priority.
// ---------------------------------------------------------------------------

export type GfxQuality = "auto" | "low" | "medium" | "high";
const QUALITY_KEY = "neonpurge.gfxQuality";

export function gfxQuality(): GfxQuality {
  try {
    const q = localStorage.getItem(QUALITY_KEY);
    if (q === "low" || q === "medium" || q === "high") return q;
  } catch { /* private mode */ }
  return "auto";
}
export function setGfxQuality(q: GfxQuality): void {
  try { localStorage.setItem(QUALITY_KEY, q); } catch { /* private mode */ }
}

export interface QualityCaps {
  /** Shadow map edge in texels; 0 = no shadows. */
  shadowSize: number;
  /** MSAA samples on the post-processing scene target (0 = none). */
  msaa: number;
  /** Screen-space AO pass. */
  ao: boolean;
  /** Ceiling for the internal render resolution (device pixel ratio cap). */
  pixelCap: number;
  /** Lowest cost-ladder level allowed (low pins L2: no shadows, no post). */
  minLevel: number;
  /** Photographed HDRI environment on day maps. */
  hdri: boolean;
}

/** Resolve the preset against the hardware probe. `weak` = weakHardware(),
 *  `weakGpu` = integrated/software GPU. lowSpec / TV are handled by the
 *  renderer before this is consulted (they always win). */
export function qualityCaps(q: GfxQuality, weak: boolean, weakGpu: boolean): QualityCaps {
  switch (q) {
    case "low":
      return { shadowSize: 0, msaa: 0, ao: false, pixelCap: 1.0, minLevel: 2, hdri: false };
    case "medium":
      return { shadowSize: 1024, msaa: 2, ao: false, pixelCap: 1.25, minLevel: 0, hdri: false };
    case "high":
      return { shadowSize: 2048, msaa: 4, ao: true, pixelCap: 1.5, minLevel: 0, hdri: true };
    default:
      // AUTO: what shipped before, plus MSAA on the post target for real GPUs
      // (the composer path had NO antialiasing at all). AO and the 2048 map
      // stay opt-in (High) — both cost GPU time every frame.
      return {
        shadowSize: weak ? 512 : 1024,
        msaa: weakGpu ? 0 : weak ? 2 : 4,
        ao: false,
        pixelCap: 1.5,
        minLevel: 0,
        hdri: false,
      };
  }
}

// ---------------------------------------------------------------------------
// LIVE TARGET — the running GameRenderer registers itself here so the debug
// panel can reach it without importing renderer.ts (which would drag the
// whole renderer into the menu bundle).
// ---------------------------------------------------------------------------
export interface GfxTarget {
  applyGfxConfig(): void;
  gfxStatus(): string;
}
let target: GfxTarget | null = null;
export function setGfxTarget(t: GfxTarget | null): void { target = t; }
export function gfxTarget(): GfxTarget | null { return target; }
